import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Plus, Pencil, Trash2, X, Save, Calendar, Users, Mail, Phone, MapPin, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

const empty = {
  title: "",
  slug: "",
  description: "",
  long_description: "",
  category: "potager",
  image: "",
  location: "Les Jardins d'Éole · Istres",
  starts_at: "",
  duration_min: 90,
  max_seats: 10,
  price_cents: 0,
  is_published: true,
  related_product_slugs: [],
  promo_code: "",
  promo_label: "",
};

const CATEGORIES = ["potager", "taille", "conception", "technique", "deco", "autre"];

function fmtFr(iso) {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      weekday: "short", day: "numeric", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function toDatetimeLocal(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60000);
    return local.toISOString().slice(0, 16);
  } catch { return ""; }
}

export default function WorkshopsAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [view, setView] = useState(null);     // workshop being inspected for registrations
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/jardins-ole/admin/workshops");
      setItems(r.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const loadRegs = async (w) => {
    setView(w);
    const r = await api.get(`/jardins-ole/admin/workshops/${w.id}/registrations`);
    setRegs(r.data || []);
  };

  const save = async () => {
    if (!edit.title) return toast.error("Titre obligatoire");
    if (!edit.starts_at) return toast.error("Date & heure obligatoires");
    setSaving(true);
    try {
      const body = {
        ...edit,
        // datetime-local → full ISO with seconds + timezone
        starts_at: new Date(edit.starts_at).toISOString(),
        duration_min: Number(edit.duration_min) || 90,
        max_seats: Number(edit.max_seats) || 10,
        price_cents: Number(edit.price_cents) || 0,
      };
      if (edit.id) await api.put(`/jardins-ole/admin/workshops/${edit.id}`, body);
      else await api.post("/jardins-ole/admin/workshops", body);
      toast.success("Atelier enregistré");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally { setSaving(false); }
  };

  const remove = async (w) => {
    if (!window.confirm(`Supprimer l'atelier « ${w.title} » et toutes ses inscriptions ?`)) return;
    await api.delete(`/jardins-ole/admin/workshops/${w.id}`);
    toast.success("Atelier supprimé");
    load();
  };

  const removeReg = async (rid) => {
    if (!window.confirm("Supprimer cette inscription ?")) return;
    await api.delete(`/jardins-ole/admin/workshops/${view.id}/registrations/${rid}`);
    setRegs(regs.filter(r => r.id !== rid));
    load();
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-workshops-page">
      <header className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold mb-1">Custom · Jardins d'Éole</div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)] flex items-center gap-2">
            <Calendar className="w-6 h-6" /> Ateliers & inscriptions
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Gérez vos ateliers gratuits et consultez les inscriptions reçues.</p>
        </div>
        <Button onClick={() => setEdit({ ...empty })} className="bg-[color:var(--brand-red)]" data-testid="new-workshop-btn">
          <Plus className="w-4 h-4 mr-1" /> Nouvel atelier
        </Button>
      </header>

      {loading ? (
        <div className="text-center py-20 text-sm text-[hsl(var(--muted-foreground))]">
          <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 border-2 border-dashed rounded-3xl">
          <Calendar className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))]/30 mb-2" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Aucun atelier programmé.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(w => {
            const taken = (w.max_seats || 10) - (w.seats_left ?? 0);
            const past = new Date(w.starts_at) < new Date();
            const fillPct = ((taken / (w.max_seats || 10)) * 100).toFixed(0);
            return (
              <Card key={w.id} className="overflow-hidden border shadow-hover" data-testid={`workshop-card-${w.slug}`}>
                {w.image && <img src={w.image} className="h-36 w-full object-cover" alt={w.title} />}
                <div className="p-4">
                  <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-wider font-bold mb-2">
                    {w.category && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{w.category}</span>}
                    {!w.is_published && <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">Brouillon</span>}
                    {past && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Passé</span>}
                  </div>
                  <h3 className="font-display font-bold text-[color:var(--brand-navy)] line-clamp-2 mb-1">{w.title}</h3>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1.5 mb-3">
                    <Calendar className="w-3 h-3" /> {fmtFr(w.starts_at)}
                  </div>

                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs font-semibold mb-1">
                      <span>{taken} / {w.max_seats} inscrits</span>
                      <span className="text-[hsl(var(--muted-foreground))]">{fillPct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                      <div
                        className={`h-full transition-all ${fillPct >= 100 ? "bg-red-500" : fillPct >= 70 ? "bg-amber-500" : "bg-emerald-500"}`}
                        style={{ width: `${Math.min(100, fillPct)}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" onClick={() => loadRegs(w)} data-testid={`view-regs-${w.slug}`}>
                      <Users className="w-3 h-3 mr-1" /> Inscrits ({taken})
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setEdit({ ...w, starts_at: toDatetimeLocal(w.starts_at) })}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => remove(w)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {edit && <Editor edit={edit} setEdit={setEdit} save={save} saving={saving} />}
      {view && <RegistrationsModal workshop={view} regs={regs} onClose={() => setView(null)} onDelete={removeReg} />}
    </div>
  );
}

function Editor({ edit, setEdit, save, saving }) {
  const set = (k, v) => setEdit({ ...edit, [k]: v });
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-2 md:p-4 overflow-y-auto" onClick={() => setEdit(null)}>
      <div className="bg-white rounded-2xl w-full max-w-3xl my-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="font-display font-bold text-xl text-[color:var(--brand-navy)]">{edit.id ? "Éditer" : "Nouvel"} atelier</h3>
          <button onClick={() => setEdit(null)} aria-label="Fermer"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <div>
            <Label>Titre *</Label>
            <Input value={edit.title} onChange={e => set("title", e.target.value)} placeholder="Atelier potager : démarrer ses semis…" data-testid="workshop-title-input"/>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Date &amp; heure *</Label>
              <Input type="datetime-local" value={edit.starts_at || ""} onChange={e => set("starts_at", e.target.value)} data-testid="workshop-date-input"/>
            </div>
            <div>
              <Label>Catégorie</Label>
              <select className="w-full h-10 px-3 rounded-md border border-[hsl(var(--border))] text-sm" value={edit.category} onChange={e => set("category", e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label>Durée (minutes)</Label>
              <Input type="number" min={15} step={15} value={edit.duration_min} onChange={e => set("duration_min", e.target.value)}/>
            </div>
            <div>
              <Label>Places max</Label>
              <Input type="number" min={1} value={edit.max_seats} onChange={e => set("max_seats", e.target.value)} data-testid="workshop-max-seats-input"/>
            </div>
            <div>
              <Label>Prix (centimes — 0 = gratuit)</Label>
              <Input type="number" min={0} value={edit.price_cents} onChange={e => set("price_cents", e.target.value)}/>
            </div>
            <div>
              <Label>Lieu</Label>
              <Input value={edit.location} onChange={e => set("location", e.target.value)} placeholder="Les Jardins d'Éole · Istres"/>
            </div>
          </div>
          <div>
            <Label>Image (URL ou /uploads/cms/…)</Label>
            <Input value={edit.image} onChange={e => set("image", e.target.value)} placeholder="/uploads/cms/jardins-ole/scraped/…"/>
            {edit.image && <img src={edit.image} className="mt-2 h-32 rounded-md object-cover" alt=""/>}
          </div>
          <div>
            <Label>Description courte</Label>
            <Textarea rows={2} value={edit.description} onChange={e => set("description", e.target.value)}/>
          </div>
          <div>
            <Label>Description longue (programme détaillé)</Label>
            <Textarea rows={6} value={edit.long_description} onChange={e => set("long_description", e.target.value)}/>
          </div>
          <div className="flex items-center gap-2">
            <input id="is_published" type="checkbox" checked={!!edit.is_published} onChange={e => set("is_published", e.target.checked)}/>
            <label htmlFor="is_published" className="text-sm font-semibold">Publier cet atelier (visible publiquement)</label>
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-3">
            <div className="flex items-baseline justify-between">
              <h4 className="font-display font-bold text-sm text-[color:var(--brand-navy)]">📧 Email de confirmation — cross-sell</h4>
              <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Optionnel</span>
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              Suggérez 2 produits pertinents et un code promo dans l'email automatique envoyé après inscription.
              Taux d'ouverture moyen : ~80 %.
            </p>
            <div>
              <Label>Slugs produits liés (1 par ligne, max 2)</Label>
              <Textarea
                rows={2}
                value={(edit.related_product_slugs || []).join("\n")}
                onChange={e => set("related_product_slugs", e.target.value.split("\n").map(s => s.trim()).filter(Boolean).slice(0, 2))}
                placeholder={"graines-de-tomates-anciennes-coeur-de-boeuf\ngraines-de-basilic-grand-vert"}
                data-testid="workshop-related-products"
              />
              <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                Trouvez les slugs dans Admin → Produits (URL : /shop/<em>slug</em>).
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Code promo</Label>
                <Input
                  value={edit.promo_code}
                  onChange={e => set("promo_code", e.target.value.toUpperCase())}
                  placeholder="ATELIER10"
                  data-testid="workshop-promo-code"
                />
              </div>
              <div>
                <Label>Libellé promo (affiché sous le code)</Label>
                <Input
                  value={edit.promo_label}
                  onChange={e => set("promo_label", e.target.value)}
                  placeholder="−10 % sur les graines bio · valable 30 jours"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t">
          <Button variant="outline" onClick={() => setEdit(null)}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-navy)] font-bold" data-testid="save-workshop-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Enregistrement…</> : <><Save className="w-4 h-4 mr-1" />Enregistrer</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RegistrationsModal({ workshop, regs, onClose, onDelete }) {
  const total = regs.reduce((sum, r) => sum + (r.seats || 1), 0);
  const exportCsv = () => {
    const head = "Date,Prénom,Nom,Email,Téléphone,Places,Note,Statut\n";
    const body = regs.map(r => [
      r.created_at, r.first_name, r.last_name, r.email, r.phone,
      r.seats, (r.notes || "").replace(/\n/g, " "), r.status,
    ].map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([head + body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `inscriptions-${workshop.slug}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl my-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="font-display font-bold text-xl text-[color:var(--brand-navy)]">Inscriptions · {workshop.title}</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{regs.length} inscription(s) · {total} place(s) réservée(s) sur {workshop.max_seats}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={regs.length === 0} data-testid="export-regs-csv">
              <FileText className="w-3 h-3 mr-1"/> Export CSV
            </Button>
            <button onClick={onClose} aria-label="Fermer"><X className="w-5 h-5"/></button>
          </div>
        </div>
        <div className="p-5 max-h-[70vh] overflow-y-auto">
          {regs.length === 0 ? (
            <div className="text-center py-12 text-sm text-[hsl(var(--muted-foreground))]">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-30"/>Pas encore d'inscription pour cet atelier.
            </div>
          ) : (
            <div className="space-y-2.5">
              {regs.map(r => (
                <div key={r.id} className="flex items-start gap-3 p-3 rounded-xl border bg-white" data-testid={`reg-row-${r.id}`}>
                  <div className="w-10 h-10 rounded-full bg-[color:var(--brand-navy)] text-white flex items-center justify-center font-bold flex-shrink-0">
                    {(r.first_name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-[color:var(--brand-navy)]">{r.first_name} {r.last_name}</div>
                    <div className="flex flex-wrap gap-2.5 text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                      <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-[color:var(--brand-navy)]"><Mail className="w-3 h-3"/>{r.email}</a>
                      {r.phone && <a href={`tel:${r.phone.replace(/\s/g,"")}`} className="inline-flex items-center gap-1 hover:text-[color:var(--brand-navy)]"><Phone className="w-3 h-3"/>{r.phone}</a>}
                      <span className="inline-flex items-center gap-1"><Users className="w-3 h-3"/>{r.seats} place{r.seats>1?"s":""}</span>
                    </div>
                    {r.notes && <p className="text-xs text-[hsl(var(--muted-foreground))] italic mt-1.5">« {r.notes} »</p>}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => onDelete(r.id)} title="Supprimer">
                    <Trash2 className="w-3 h-3"/>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
